import React, { useState, useEffect, useRef } from 'react';
import { Table, Button, Space, Modal, message, Input, Select, Card, Tag, Image, DatePicker, InputNumber, Popconfirm, Upload, Radio, Spin } from 'antd';
import { PlusOutlined, EditOutlined, DeleteOutlined, SearchOutlined, EyeOutlined, UploadOutlined, LinkOutlined, PictureOutlined } from '@ant-design/icons';
import { eventService, eventCategoryService, eventRegistrationService } from '../../services';
import RichTextEditor from '../../components/RichTextEditor';
import apiClient from '../../services/api';
import dayjs from 'dayjs';

const { Option } = Select;

const EventList = () => {
  const [events, setEvents] = useState([]);
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(false);
  const [modalVisible, setModalVisible] = useState(false);
  const [editingEvent, setEditingEvent] = useState(null);
  const [pagination, setPagination] = useState({ current: 1, pageSize: 10, total: 0 });
  const [filters, setFilters] = useState({
    search: '',
    idphanloai: null,
    trangthai: null
  });
  const [form, setForm] = useState({
    tensukien: '',
    idphanloai: null,
    anhdaidien: '',
    vitri: '',
    noidung: '',
    date: null,
    gia: 0,
    mucdouutien: 1
  });
  const [contentHtml, setContentHtml] = useState('');
  const [showContentPreview, setShowContentPreview] = useState(false);
  const [imageMethod, setImageMethod] = useState('url');
  const [uploadedImageUrl, setUploadedImageUrl] = useState('');
  const [uploading, setUploading] = useState(false);
  const richTextEditorRef = useRef(null);
  const [registrationModalVisible, setRegistrationModalVisible] = useState(false);
  const [selectedEvent, setSelectedEvent] = useState(null);
  const [registrations, setRegistrations] = useState([]);
  const [registrationLoading, setRegistrationLoading] = useState(false);

  useEffect(() => {
    fetchEvents();
    fetchCategories();
  }, [pagination.current, pagination.pageSize, filters]);

  const fetchEvents = async () => {
    setLoading(true);
    try {
      const params = {
        page: pagination.current,
        pageSize: pagination.pageSize,
        ...filters
      };
      const response = await eventService.getAll(params);
      if (response && Array.isArray(response)) {
        setEvents(response);
        if (response.length > 0) {
          setPagination(prev => ({ ...prev, total: response[0].TotalRecords || 0 }));
        }
      }
    } catch (error) {
      message.error('Lỗi khi tải danh sách sự kiện');
    } finally {
      setLoading(false);
    }
  };

  const fetchCategories = async () => {
    try {
      const response = await eventCategoryService.getAll();
      if (response && Array.isArray(response)) {
        setCategories(response);
      }
    } catch (error) {
      console.error('Lỗi khi tải danh sách phân loại');
    }
  };

  const handleAdd = () => {
    setEditingEvent(null);
    setForm({
      tensukien: '',
      idphanloai: null,
      anhdaidien: '',
      vitri: '',
      noidung: '',
      date: null,
      gia: 0,
      mucdouutien: 1
    });
    setContentHtml('');
    setShowContentPreview(false);
    setImageMethod('url');
    setUploadedImageUrl('');
    setModalVisible(true);
  };

  const handleEdit = (record) => {
    setEditingEvent(record);
    setForm({
      tensukien: record.TenSuKien || '',
      idphanloai: record.IdPhanLoai || null,
      anhdaidien: record.AnhDaiDien || '',
      vitri: record.ViTri || '',
      noidung: record.NoiDung || '',
      date: record.Date ? dayjs(record.Date) : null,
      gia: record.Gia || 0,
      mucdouutien: record.MucDoUuTien || 1
    });
    setContentHtml(record.NoiDung || '');
    if (record.AnhDaiDien) {
      setImageMethod('url');
      setUploadedImageUrl('');
    }
    setModalVisible(true);
  };

  const handleDelete = async (id) => {
    try {
      await eventService.delete(id);
      message.success('Xóa sự kiện thành công');
      fetchEvents();
    } catch (error) {
      message.error('Lỗi khi xóa sự kiện');
    }
  };

  const handleStatusChange = async (id, newStatus) => {
    try {
      await eventService.updateStatus(id, newStatus);
      message.success('Cập nhật trạng thái thành công');
      fetchEvents();
    } catch (error) {
      message.error('Lỗi khi cập nhật trạng thái');
    }
  };

  const handleViewRegistrations = async (event) => {
    setSelectedEvent(event);
    setRegistrationModalVisible(true);
    await fetchRegistrations(event.IdSuKien);
  };

  const fetchRegistrations = async (eventId) => {
    setRegistrationLoading(true);
    try {
      const response = await eventRegistrationService.getRegistrations({ idsukien: eventId });
      setRegistrations(Array.isArray(response) ? response : []);
    } catch (error) {
      message.error('Lỗi khi tải danh sách đăng ký');
      setRegistrations([]);
    } finally {
      setRegistrationLoading(false);
    }
  };

  const handleRegistrationStatusChange = async (registrationId, newStatus) => {
    try {
      await eventRegistrationService.updateStatus(registrationId, newStatus);
      message.success('Cập nhật trạng thái thành công');
      fetchRegistrations(selectedEvent.IdSuKien);
    } catch (error) {
      message.error('Lỗi khi cập nhật trạng thái');
    }
  };

  const handleDeleteRegistration = async (registrationId) => {
    try {
      await eventRegistrationService.delete(registrationId);
      message.success('Xóa đăng ký thành công');
      fetchRegistrations(selectedEvent.IdSuKien);
    } catch (error) {
      message.error('Lỗi khi xóa đăng ký');
    }
  };

  const [submitting, setSubmitting] = useState(false);

  const handleSubmit = async () => {
    if (!form.tensukien.trim()) {
      message.error('Vui lòng nhập tên sự kiện');
      return;
    }

    if (submitting) return;
    setSubmitting(true);

    const submitData = {
      IdSuKien: editingEvent ? editingEvent.IdSuKien : Date.now(),
      tensukien: form.tensukien,
      idphanloai: form.idphanloai,
      anhdaidien: form.anhdaidien || uploadedImageUrl || '',
      vitri: form.vitri || '',
      noidung: contentHtml || form.noidung || '',
      date: form.date ? form.date.format('YYYY-MM-DD') : null,
      gia: form.gia || 0,
      mucdouutien: form.mucdouutien || 1
    };
    
    // Loại bỏ các field null/undefined
    Object.keys(submitData).forEach(key => {
      if (submitData[key] === null || submitData[key] === undefined) {
        delete submitData[key];
      }
    });

    console.log('📤 Submitting event data:', submitData);
    console.log('📤 Is editing:', editingEvent ? 'Yes' : 'No');

    try {
      if (editingEvent) {
        const response = await eventService.update(editingEvent.IdSuKien, submitData);
        console.log('✅ Update response:', response);
        message.success('Cập nhật sự kiện thành công');
      } else {
        const response = await eventService.create(submitData);
        console.log('✅ Create response:', response);
        message.success('Thêm sự kiện thành công');
      }
      setModalVisible(false);
      fetchEvents();
    } catch (error) {
      console.error('❌ Submit error:', error);
      console.error('❌ Error response:', error.response?.data);
      console.error('❌ Error status:', error.response?.status);
      console.error('❌ Error headers:', error.response?.headers);
      console.error('❌ Request config:', error.config);
      
      const errorMessage = error.response?.data?.message || 
                          error.response?.data?.error || 
                          error.message || 
                          'Lỗi không xác định';
      
      // Hiển thị chi tiết lỗi 400
      if (error.response?.status === 400) {
        console.error('❌ 400 Bad Request Details:', {
          url: error.config?.url,
          method: error.config?.method,
          data: error.config?.data,
          params: error.config?.params
        });
      }
      
      message.error(editingEvent ? `Lỗi khi cập nhật: ${errorMessage}` : `Lỗi khi thêm mới: ${errorMessage}`);
    } finally {
      setSubmitting(false);
    }
  };

  const handleTableChange = (paginationInfo) => {
    setPagination({
      ...pagination,
      current: paginationInfo.current,
      pageSize: paginationInfo.pageSize
    });
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'Đang hoạt động': return 'green';
      case 'Đã Xong': return 'blue';
      case 'Hủy': return 'red';
      default: return 'default';
    }
  };

  const columns = [
    {
      title: 'ID',
      dataIndex: 'IdSuKien',
      key: 'IdSuKien',
      width: 80,
    },
    {
      title: 'Ảnh',
      dataIndex: 'AnhDaiDien',
      key: 'AnhDaiDien',
      width: 100,
      render: (text) => (
        text ? <Image width={60} height={40} src={text} style={{ objectFit: 'cover' }} /> : 'Không có'
      ),
    },
    {
      title: 'Tên Sự Kiện',
      dataIndex: 'TenSuKien',
      key: 'TenSuKien',
    },
    {
      title: 'Phân Loại',
      dataIndex: 'TenPhanLoai',
      key: 'TenPhanLoai',
    },
    {
      title: 'Vị Trí',
      dataIndex: 'ViTri',
      key: 'ViTri',
    },
    {
      title: 'Ngày',
      dataIndex: 'Date',
      key: 'Date',
      render: (text) => text ? dayjs(text).format('DD/MM/YYYY') : '',
    },
    {
      title: 'Giá',
      dataIndex: 'Gia',
      key: 'Gia',
      render: (text) => new Intl.NumberFormat('vi-VN', { style: 'currency', currency: 'VND' }).format(text || 0),
    },
    {
      title: 'Ưu Tiên',
      dataIndex: 'MucDoUuTien',
      key: 'MucDoUuTien',
    },
    {
      title: 'Trạng Thái',
      dataIndex: 'TrangThai',
      key: 'TrangThai',
      render: (status, record) => (
        <Select
          value={status}
          style={{ width: 120 }}
          onChange={(value) => handleStatusChange(record.IdSuKien, value)}
        >
          <Option value="Đang hoạt động">
            <Tag color="green">Đang hoạt động</Tag>
          </Option>
          <Option value="Đã Xong">
            <Tag color="blue">Đã Xong</Tag>
          </Option>
          <Option value="Hủy">
            <Tag color="red">Hủy</Tag>
          </Option>
        </Select>
      ),
    },
    {
      title: 'Thao tác',
      key: 'action',
      width: 200,
      render: (_, record) => (
        <Space size="small">
          <Button
            type="default"
            size="small"
            onClick={() => handleViewRegistrations(record)}
          >
            Đăng ký
          </Button>
          <Button
            type="primary"
            size="small"
            icon={<EditOutlined />}
            onClick={() => handleEdit(record)}
          />
          <Popconfirm
            title="Bạn có chắc chắn muốn xóa?"
            onConfirm={() => handleDelete(record.IdSuKien)}
            okText="Có"
            cancelText="Không"
          >
            <Button
              type="primary"
              danger
              size="small"
              icon={<DeleteOutlined />}
            />
          </Popconfirm>
        </Space>
      ),
    },
  ];

  return (
    <div style={{ padding: 24 }}>
      <Card title="Quản lý Sự Kiện">
        <div style={{ marginBottom: 16, display: 'flex', gap: 16, flexWrap: 'wrap' }}>
          <Input
            placeholder="Tìm kiếm theo tên sự kiện..."
            prefix={<SearchOutlined />}
            value={filters.search}
            onChange={(e) => setFilters({ ...filters, search: e.target.value })}
            style={{ width: 250 }}
          />
          <Select
            placeholder="Chọn phân loại"
            value={filters.idphanloai}
            onChange={(value) => setFilters({ ...filters, idphanloai: value })}
            style={{ width: 200 }}
            allowClear
          >
            {categories.map(cat => (
              <Option key={cat.id} value={cat.id}>{cat.TenSuKien}</Option>
            ))}
          </Select>
          <Select
            placeholder="Chọn trạng thái"
            value={filters.trangthai}
            onChange={(value) => setFilters({ ...filters, trangthai: value })}
            style={{ width: 150 }}
            allowClear
          >
            <Option value="Đang hoạt động">Đang hoạt động</Option>
            <Option value="Đã Xong">Đã Xong</Option>
            <Option value="Hủy">Hủy</Option>
          </Select>
          <Button
            type="primary"
            icon={<PlusOutlined />}
            onClick={handleAdd}
          >
            Thêm Sự Kiện
          </Button>
        </div>

        <Table
          columns={columns}
          dataSource={events}
          rowKey="IdSuKien"
          loading={loading}
          pagination={{
            ...pagination,
            showSizeChanger: true,
            showQuickJumper: true,
            showTotal: (total) => `Tổng ${total} sự kiện`,
          }}
          onChange={handleTableChange}
        />

        <Modal
          title={editingEvent ? 'Sửa Sự Kiện' : 'Thêm Sự Kiện'}
          open={modalVisible}
          onOk={handleSubmit}
          onCancel={() => setModalVisible(false)}
          okText={editingEvent ? 'Cập nhật' : 'Thêm'}
          cancelText="Hủy"
          confirmLoading={submitting}
          width={600}
        >
          <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
            <div>
              <label>Tên Sự Kiện *</label>
              <Input
                value={form.tensukien}
                onChange={(e) => setForm({ ...form, tensukien: e.target.value })}
                placeholder="Nhập tên sự kiện"
              />
            </div>
            <div>
              <label>Phân Loại</label>
              <Select
                value={form.idphanloai}
                onChange={(value) => setForm({ ...form, idphanloai: value })}
                placeholder="Chọn phân loại"
                style={{ width: '100%' }}
              >
                {categories.map(cat => (
                  <Option key={cat.id} value={cat.id}>{cat.TenSuKien}</Option>
                ))}
              </Select>
            </div>
            <div>
              <label>Ảnh Đại Diện</label>
              <Radio.Group 
                value={imageMethod} 
                onChange={(e) => {
                  setImageMethod(e.target.value);
                  if (e.target.value === 'url') {
                    setUploadedImageUrl('');
                  } else {
                    setForm({ ...form, anhdaidien: '' });
                  }
                }}
                style={{ marginBottom: 16 }}
              >
                <Radio.Button value="url">
                  <LinkOutlined /> URL
                </Radio.Button>
                <Radio.Button value="upload">
                  <UploadOutlined /> Upload
                </Radio.Button>
              </Radio.Group>

              {imageMethod === 'url' ? (
                <Input
                  value={form.anhdaidien}
                  onChange={(e) => setForm({ ...form, anhdaidien: e.target.value })}
                  placeholder="https://example.com/image.jpg"
                  prefix={<LinkOutlined />}
                />
              ) : (
                <div>
                  <Upload
                    name="image"
                    listType="picture-card"
                    showUploadList={false}
                    beforeUpload={async (file) => {
                      setUploading(true);
                      try {
                        const formData = new FormData();
                        formData.append('image', file);
                        
                        const response = await apiClient.post('/api/uploads/image', formData, {
                          headers: { 'Content-Type': 'multipart/form-data' }
                        });
                        
                        if (response.data.success) {
                          const imageUrl = response.data.data.url;
                          setUploadedImageUrl(imageUrl);
                          setForm({ ...form, anhdaidien: imageUrl });
                          message.success('Upload ảnh thành công!');
                        } else {
                          message.error('Upload ảnh thất bại!');
                        }
                      } catch (error) {
                        message.error('Không thể upload ảnh. Vui lòng thử lại!');
                      } finally {
                        setUploading(false);
                      }
                      return false;
                    }}
                    disabled={uploading}
                    style={{ width: 150, height: 150 }}
                  >
                    {uploadedImageUrl ? (
                      <Image
                        src={uploadedImageUrl}
                        alt="Uploaded"
                        style={{ width: '100%', height: '100%', objectFit: 'cover' }}
                        preview={true}
                      />
                    ) : (
                      <div>
                        {uploading ? (
                          <Spin />
                        ) : (
                          <>
                            <PictureOutlined style={{ fontSize: '24px', color: '#999' }} />
                            <div style={{ marginTop: 8, color: '#999' }}>
                              Click để upload
                            </div>
                          </>
                        )}
                      </div>
                    )}
                  </Upload>
                  
                  {uploadedImageUrl && (
                    <div style={{ marginTop: 8, fontSize: '12px', color: '#666', wordBreak: 'break-all' }}>
                      {uploadedImageUrl}
                    </div>
                  )}
                </div>
              )}
            </div>
            <div>
              <label>Vị Trí</label>
              <Input
                value={form.vitri}
                onChange={(e) => setForm({ ...form, vitri: e.target.value })}
                placeholder="Nhập vị trí tổ chức"
              />
            </div>
            <div>
              <label>
                <Space>
                  <span>Nội Dung</span>
                  <Button 
                    type="link" 
                    size="small" 
                    icon={<EyeOutlined />}
                    onClick={() => setShowContentPreview(!showContentPreview)}
                  >
                    {showContentPreview ? 'Chỉnh sửa' : 'Xem trước'}
                  </Button>
                </Space>
              </label>
              {showContentPreview ? (
                <div 
                  style={{
                    border: '1px solid #d9d9d9',
                    borderRadius: '6px',
                    padding: '16px',
                    minHeight: '200px',
                    background: '#fff',
                    fontSize: '14px',
                    wordBreak: 'break-word'
                  }}
                  dangerouslySetInnerHTML={{ __html: contentHtml }}
                />
              ) : (
                <RichTextEditor
                  ref={richTextEditorRef}
                  value={contentHtml}
                  onChange={(content) => {
                    setContentHtml(content);
                    setForm({ ...form, noidung: content });
                  }}
                  placeholder="Nhập nội dung chi tiết sự kiện. Bạn có thể định dạng văn bản, thêm hình ảnh, liên kết..."
                />
              )}
            </div>
            <div style={{ display: 'flex', gap: 16 }}>
              <div style={{ flex: 1 }}>
                <label>Ngày Tổ Chức</label>
                <DatePicker
                  value={form.date}
                  onChange={(date) => setForm({ ...form, date })}
                  style={{ width: '100%' }}
                  format="DD/MM/YYYY"
                />
              </div>
              <div style={{ flex: 1 }}>
                <label>Giá (VND)</label>
                <InputNumber
                  value={form.gia}
                  onChange={(value) => setForm({ ...form, gia: value || 0 })}
                  style={{ width: '100%' }}
                  formatter={value => `${value}`.replace(/\B(?=(\d{3})+(?!\d))/g, ',')}
                  parser={value => value.replace(/\$\s?|(,*)/g, '')}
                />
              </div>
            </div>
            <div>
              <label>Mức Độ Ưu Tiên</label>
              <InputNumber
                value={form.mucdouutien}
                onChange={(value) => setForm({ ...form, mucdouutien: value || 1 })}
                style={{ width: '100%' }}
                min={1}
                max={999}
                placeholder="Nhập số ưu tiên"
                addonAfter="Số càng lớn càng ưu tiên"
              />
            </div>
          </div>
        </Modal>

        <Modal
          title={`Quản lý đăng ký - ${selectedEvent?.TenSuKien}`}
          open={registrationModalVisible}
          onCancel={() => setRegistrationModalVisible(false)}
          footer={null}
          width={1000}
        >
          <Table
            columns={[
              {
                title: 'ID',
                dataIndex: 'id',
                key: 'id',
                width: 80,
              },
              {
                title: 'Họ tên',
                dataIndex: 'hoten',
                key: 'hoten',
              },
              {
                title: 'Email',
                dataIndex: 'email',
                key: 'email',
              },
              {
                title: 'SĐT',
                dataIndex: 'sdt',
                key: 'sdt',
              },
              {
                title: 'Ngày đăng ký',
                dataIndex: 'ngaydangky',
                key: 'ngaydangky',
                render: (text) => text ? dayjs(text).format('DD/MM/YYYY HH:mm') : '',
              },
              {
                title: 'Trạng thái',
                dataIndex: 'trangthai',
                key: 'trangthai',
                render: (status, record) => (
                  <Select
                    value={status}
                    style={{ width: 120 }}
                    onChange={(value) => handleRegistrationStatusChange(record.id, value)}
                  >
                    <Option value="Chưa Duyệt">
                      <Tag color="orange">Chưa Duyệt</Tag>
                    </Option>
                    <Option value="Đã Duyệt">
                      <Tag color="green">Đã Duyệt</Tag>
                    </Option>
                    <Option value="Bị Từ Chối">
                      <Tag color="red">Bị Từ Chối</Tag>
                    </Option>
                  </Select>
                ),
              },
              {
                title: 'Thao tác',
                key: 'action',
                width: 100,
                render: (_, record) => (
                  <Popconfirm
                    title="Bạn có chắc chắn muốn xóa?"
                    onConfirm={() => handleDeleteRegistration(record.id)}
                    okText="Có"
                    cancelText="Không"
                  >
                    <Button
                      type="primary"
                      danger
                      size="small"
                      icon={<DeleteOutlined />}
                    />
                  </Popconfirm>
                ),
              },
            ]}
            dataSource={registrations}
            rowKey="id"
            loading={registrationLoading}
            pagination={{
              pageSize: 10,
              showSizeChanger: true,
              showQuickJumper: true,
              showTotal: (total) => `Tổng ${total} đăng ký`,
            }}
          />
        </Modal>
      </Card>
    </div>
  );
};

export default EventList;